import urlStateService from "../../services/urlStateService";
import { handleParsedState } from "../helpers/stateHelpers";
import {
  buildFetchParams,
  shouldRefetchOnUpdate,
} from "../helpers/filterHelpers";

/**
 * Coordinates complex multi-context operations for the learning catalogue
 */
export const catalogueCoordinator = {
  /**
   * Initializes all data by fetching catalogue data and categories
   * @param {Object} dependencies - Object containing contexts and services
   * @param {Object} dependencies.filterContext - Filter context
   * @param {Object} dependencies.uiContext - UI context
   * @param {Object} dependencies.dataContext - Data context
   * @returns {Promise<void>}
   */
  async initializeData({ filterContext, uiContext, dataContext }) {
    const parsed = urlStateService.parseStateFromUrl(window.location);
    if (parsed) {
      urlStateService.setHydrating(true);
      try {
        await handleParsedState(parsed, {
          filterContext,
          uiContext,
          dataContext,
        });
      } finally {
        urlStateService.setHydrating(false);
      }
    } else {
      const params = buildFetchParams(
        filterContext.filters,
        uiContext.uiState.page || 1,
      );
      await dataContext.fetchCatalogueData(params);
    }
    await dataContext.fetchOtherCategories();

    urlStateService.initPopState(async (nextParsed) => {
      if (!nextParsed) return;
      urlStateService.setHydrating(true);
      try {
        await handleParsedState(nextParsed, {
          filterContext,
          uiContext,
          dataContext,
        });
      } finally {
        urlStateService.setHydrating(false);
      }
    });
  },

  /**
   * Updates filters with optional refetch and URL sync coordination
   * Only refetches if date/session filters change (server-side filters)
   * Otherwise uses client-side filtering
   * @param {Object} dependencies - Object containing contexts, services, and parameters
   * @param {Object} dependencies.filterContext - Filter context
   * @param {Object} dependencies.uiContext - UI context
   * @param {Object} dependencies.dataContext - Data context
   * @param {Function} dependencies.syncUrlState - Function to sync URL state
   * @param {Object} updates - Filter updates to apply
   * @returns {Promise<void>}
   */
  async updateFiltersWithRefetch({
    filterContext,
    uiContext,
    dataContext,
    syncUrlState,
    updates,
  }) {
    filterContext.updateFilters(updates);
    const next = { ...filterContext.filters, ...updates };
    let nextUi = uiContext.uiState;

    if (shouldRefetchOnUpdate(updates)) {
      nextUi = { ...uiContext.uiState, page: 1 };
      uiContext.setPage(1);
      const params = buildFetchParams(next, 1);
      await dataContext.fetchCatalogueData(params);
    } else {
      const params = buildFetchParams(next, uiContext.uiState.page);
      await dataContext.applyFiltersToData(params);
    }

    syncUrlState(next, nextUi);
  },
};
